import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./userHistory.css";
import { resolveShortId } from "../utils/userIdentity";
import {
  ChakraProvider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
  useToast,
} from "@chakra-ui/react";
import { apiUrl } from "../chat/api/config";
import { deleteConversation, fetchConversations, normalizeConversation } from "../chat/api/history";

const PAGE_SIZE = 20;
const GROUPS = ["Today", "Yesterday", "Last 7 Days", "Last 30 Days", "Older"];

const LOCALES = {
  Japanese: "ja", English: "en", Indonesian: "id", Nepali: "ne", Sinhala: "si",
  Vietnamese: "vi", Filipino: "fil", Bengali: "bn",
};

function toDate(value) {
  if (!value) return null;
  const s = String(value);
  // backend timestamps without a zone are UTC
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dayDiff(date) {
  const now = new Date();
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const b = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((a - b) / 86400000);
}

function groupOf(date) {
  if (!date) return "Older";
  const d = dayDiff(date);
  if (d <= 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d <= 7) return "Last 7 Days";
  if (d <= 30) return "Last 30 Days";
  return "Older";
}

export function relativeTime(date, langOpt) {
  if (!date) return "";
  const locale = LOCALES[langOpt] || "en";
  let rtf;
  try {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" });
  } catch (e) {
    return date.toLocaleDateString();
  }
  const sec = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(sec);
  if (abs < 60) return rtf.format(0, "second");
  if (abs < 3600) return rtf.format(Math.round(sec / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(sec / 3600), "hour");
  if (abs < 86400 * 7) return rtf.format(Math.round(sec / 86400), "day");
  try {
    return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
  } catch (e) {
    return date.toLocaleDateString();
  }
}

function sortByRecent(items) {
  return [...items].sort((a, b) => {
    const ta = toDate(a.updatedAt)?.getTime() || 0;
    const tb = toDate(b.updatedAt)?.getTime() || 0;
    return tb - ta;
  });
}

function SkeletonRows({ count = 6 }) {
  return (
    <ul className="hp-list" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="hp-item hp-item--skeleton">
          <span className="hp-skel hp-skel--title" style={{ width: `${70 - (i % 3) * 12}%` }} />
          <span className="hp-skel hp-skel--meta" />
        </li>
      ))}
    </ul>
  );
}

async function ensureUserInfo() {
  if (localStorage.getItem("userinfo")) return;
  try {
    const response = await axios.get("/oauth2/userinfo");
    sessionStorage.setItem("userinfoResponse", JSON.stringify(response.data));
    // Prefer a real email claim (Microsoft) -> local part; fall back to the bare `user` short ID (CIAM),
    // never a GUID/opaque value. Keeps history keyed on the same ID the backend derives from the JWT.
    const shortId = resolveShortId(response.data)?.toLowerCase();
    if (shortId) {
      localStorage.setItem("userinfo", shortId);
      await axios.post(apiUrl("user-logs"), { UserEmail: shortId });
    }
  } catch (err) {
    // handled silently, as before
  }
}

/**
 * Sidebar conversation history: paginated list with infinite scroll, client-side search, relative times,
 * model/year chips and previews when the backend sends them, skeleton loading, optimistic insertion of the
 * conversation that just finished a turn, and delete with confirmation.
 *
 * Props (App): openConversation(chatId) -> Promise, selectedChatId, setChildFunc, setRemoveChatFunc,
 * translate, handleNewChat, toggleSidebar, langOpt.
 */
export default function UserHistory(props) {
  const { translate: t = (s) => s, langOpt } = props;
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const cursorRef = useRef(null); // next_cursor from the backend (cursor paging); page numbers otherwise
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [openingId, setOpeningId] = useState(null);
  const [chatToDelete, setChatToDelete] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast({ position: "top-right" });
  const sentinel = useRef(null);
  const containerRef = useRef(null);
  const loadingRef = useRef(false);

  const loadPage = useCallback(async (pageToFetch) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (pageToFetch === 1) setInitialLoading(true);
    else setLoadingMore(true);
    setError(false);
    try {
      await ensureUserInfo();
      const { items: fetched, hasMore: more, nextCursor } = await fetchConversations({
        page: pageToFetch,
        cursor: pageToFetch > 1 ? cursorRef.current : null,
        limit: PAGE_SIZE,
      });
      cursorRef.current = nextCursor;
      setItems((prev) => {
        const byId = new Map(prev.map((c) => [c.id, c]));
        fetched.forEach((c) => {
          const existing = byId.get(c.id);
          // keep an optimistic entry's newer timestamp
          if (!existing || (toDate(c.updatedAt)?.getTime() || 0) >= (toDate(existing.updatedAt)?.getTime() || 0)) {
            byId.set(c.id, { ...existing, ...c });
          }
        });
        return sortByRecent([...byId.values()]);
      });
      setHasMore(more);
      setPage(pageToFetch);
    } catch (err) {
      console.error("Error fetching conversations:", err);
      setError(true);
    } finally {
      loadingRef.current = false;
      setInitialLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  // Infinite scroll: load the next page when the sentinel at the end of the list becomes visible.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !loadingRef.current) loadPage(page + 1);
    }, { root: containerRef.current, rootMargin: "120px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, page, loadPage, items.length]);

  // Optimistic insert/update when a chat turn finishes (called by App through setChildFunc).
  const upsert = useCallback((entity) => {
    if (!entity) return;
    const item = normalizeConversation(entity);
    if (!item.id) return;
    item.updatedAt = entity.Timestamp || new Date().toISOString();
    setItems((prev) => {
      const existing = prev.find((c) => c.id === item.id);
      const merged = existing
        ? { ...existing, updatedAt: item.updatedAt, model: item.model || existing.model, year: item.year || existing.year }
        : item;
      return [merged, ...prev.filter((c) => c.id !== item.id)];
    });
  }, []);

  useEffect(() => {
    if (props.setChildFunc) props.setChildFunc(() => upsert);
  }, [props.setChildFunc]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.setRemoveChatFunc) props.setRemoveChatFunc(() => () => setOpeningId(null));
  }, [props.setRemoveChatFunc]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (chatId) => {
    if (openingId) return;
    setOpeningId(chatId);
    try {
      if (props.openConversation) await props.openConversation(chatId);
      if (props.toggleSidebar && window.innerWidth <= 640) props.toggleSidebar();
    } catch (err) {
      console.error("Error loading conversation:", err);
      toast({
        title: t("Error"),
        description: t("Unable to load this conversation."),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setOpeningId(null);
    }
  };

  const confirmDelete = async () => {
    const chatId = chatToDelete;
    if (!chatId) return;
    onClose();
    setChatToDelete(null);
    const snapshot = items;
    setItems((prev) => prev.filter((c) => c.id !== chatId)); // optimistic
    try {
      await deleteConversation(chatId);
      if (props.selectedChatId === chatId && props.handleNewChat) props.handleNewChat();
      toast({ description: t("Chat Deleted successfully!"), status: "success", duration: 1500, isClosable: true });
    } catch (err) {
      setItems(snapshot);
      toast({ description: t("Something went wrong. Please try again."), status: "error", duration: 2500, isClosable: true });
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => [c.title, c.preview, c.model, c.year].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [items, query]);

  const grouped = useMemo(() => {
    const map = new Map(GROUPS.map((g) => [g, []]));
    filtered.forEach((c) => map.get(groupOf(toDate(c.updatedAt))).push(c));
    return GROUPS.map((g) => [g, map.get(g)]).filter(([, list]) => list.length);
  }, [filtered]);

  return (
    <div className="history-container hp-root" ref={containerRef}>
      <ChakraProvider resetCSS={false}>
        <Modal isCentered isOpen={isOpen} onClose={() => { onClose(); setChatToDelete(null); }}>
          <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6px)" />
          <ModalContent>
            <ModalCloseButton />
            <ModalBody>
              <h2 className="feedback-title">{t("Are you sure you want to delete this chat?")}</h2>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => { onClose(); setChatToDelete(null); }} mr={3}>{t("Close")}</Button>
              <Button colorScheme="red" onClick={confirmDelete}>{t("Delete")}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </ChakraProvider>

      <div className="hp-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Search chats")}
          aria-label={t("Search chats")}
        />
      </div>

      {initialLoading && items.length === 0 && <SkeletonRows />}

      {!initialLoading && error && items.length === 0 && (
        <div className="hp-empty">
          <span>{t("Something went wrong. Please try again.")}</span>
          <button type="button" className="hp-retry" onClick={() => loadPage(1)}>{t("Retry")}</button>
        </div>
      )}

      {!initialLoading && !error && filtered.length === 0 && (
        <div className="hp-empty">{query ? t("No matching chats") : t("No chats yet")}</div>
      )}

      {grouped.map(([group, list]) => (
        <section key={group} className="hp-group">
          <h2 className="history-heading">{t(group)}</h2>
          <ul className="hp-list history-list-v2">
            {list.map((c) => {
              const date = toDate(c.updatedAt);
              const selected = props.selectedChatId === c.id;
              const loading = openingId === c.id;
              return (
                <li
                  key={c.id}
                  className={`hp-item${selected ? " selected is-selected" : ""}${loading ? " is-loading" : ""}`}
                  aria-current={selected ? "true" : undefined}
                >
                  <button type="button" className="hp-item__main" onClick={() => open(c.id)} title={c.title}>
                    <span className="hp-item__title chat-text">{c.title || t("Untitled chat")}</span>
                    {c.preview && <span className="hp-item__preview">{c.preview}</span>}
                    <span className="hp-item__meta">
                      {loading ? <span className="hp-item__loading">{t("Loading...")}</span> : <span>{relativeTime(date, langOpt)}</span>}
                      {(c.model || c.year) && <span className="hp-chip">{[c.model, c.year].filter(Boolean).join(" ")}</span>}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="hp-item__delete delete-button"
                    aria-label={t("Delete")}
                    title={t("Delete")}
                    onClick={(e) => {
                      e.stopPropagation();
                      setChatToDelete(c.id);
                      onOpen();
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                    </svg>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {hasMore && !initialLoading && !error && (
        <div ref={sentinel} className="hp-more">
          {loadingMore ? <SkeletonRows count={2} /> : (
            <button type="button" className="hp-retry" onClick={() => loadPage(page + 1)}>{t("Load more")}</button>
          )}
        </div>
      )}
      {error && items.length > 0 && (
        <div className="hp-more">
          <button type="button" className="hp-retry" onClick={() => loadPage(page + 1)}>{t("Retry")}</button>
        </div>
      )}
    </div>
  );
}
